/**
 * Malaysia 주소 파서 (재사용) — 전체 주소를 구성요소로 분리.
 *
 * MY preset / CDD 등 "주소를 street·town·state·postcode·country 칸으로 나눠 채우는" 폼에서 공용 사용.
 *
 * 원칙:
 *   - 확신할 수 없는 요소는 잘못된 값 대신 **빈칸**으로 둔다 (requirement 3).
 *   - 원본 전체 주소는 절대 수정하지 않는다 — 이 파서는 pre-fill 용도로만.
 *
 * 두 입력을 지원:
 *   - parseMalaysianAddress(str)      : 한 줄 전체 주소 문자열 파싱 (휴리스틱)
 *   - parseMyAddressObject(obj)        : 구조화 주소 객체(Contact registeredAddress 형태) → 컴포넌트 (더 신뢰도 높음)
 */

export interface ParsedMyAddress {
  street: string;   // 번지·도로 (예: "NO. 18, JALAN BUKIT BINTANG")
  town: string;     // 도시/타운 (예: "KUALA LUMPUR")
  state: string;    // 주 (예: "JOHOR" / "WILAYAH PERSEKUTUAN KUALA LUMPUR")
  postcode: string; // 우편번호 (MY = 5자리)
  country: string;  // 국가명 (예: "Malaysia")
}

const EMPTY: ParsedMyAddress = { street: '', town: '', state: '', postcode: '', country: '' };

// 말레이시아 주(state) — 긴 이름 우선 매칭
const MY_STATES = [
  'Wilayah Persekutuan Kuala Lumpur', 'Wilayah Persekutuan Labuan', 'Wilayah Persekutuan Putrajaya',
  'Negeri Sembilan', 'Pulau Pinang', 'Kuala Lumpur', 'Putrajaya',
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Malacca', 'Pahang', 'Perak', 'Perlis',
  'Penang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu', 'Labuan',
];
// 도로/번지 키워드 — 이 키워드가 있으면 street, 없으면 town 후보
const ROAD_KW = /\b(jalan|jln|lorong|lrg|persiaran|psn|taman|tmn|lebuh|lebuhraya|no\.?|lot|blok|block|kampung|kg|bandar|bangunan|wisma|menara|tingkat|unit)\b/i;
const POSTCODE_RE = /\b(\d{5})\b/;

// 국가 base code → 이름 (알려진 것만; 미상 코드는 빈칸 = requirement 3)
const COUNTRY_CODE: Record<string, string> = { B15135: 'Malaysia' };
const KNOWN_COUNTRIES = [
  'Malaysia', 'Singapore', 'Indonesia', 'Thailand', 'Brunei', 'Philippines', 'Vietnam',
  'China', 'Hong Kong', 'India', 'Japan', 'Korea', 'United Kingdom', 'United States', 'Australia',
];

export function resolveCountry(v?: string): string {
  const s = String(v || '').trim();
  if (!s) return '';
  // 국가명은 항상 대문자로 통일 (문서/폼 표기 규칙 — 예: "MALAYSIA"). country 콤보 옵션(대문자)과도 일치.
  if (COUNTRY_CODE[s.toUpperCase()]) return COUNTRY_CODE[s.toUpperCase()].toUpperCase();
  const hit = KNOWN_COUNTRIES.find((c) => c.toUpperCase() === s.toUpperCase());
  if (hit) return hit.toUpperCase();
  if (/^B\d{4,}$/i.test(s)) return ''; // 미상 base code → 빈칸
  return s.toUpperCase(); // 그 외는 국가명으로 간주 (대문자)
}

function matchState(text: string): string {
  const up = String(text || '').toUpperCase();
  for (const st of [...MY_STATES].sort((a, b) => b.length - a.length)) {
    if (up.includes(st.toUpperCase())) return st;
  }
  return '';
}

/** 한 줄 전체 주소 문자열 → 컴포넌트 (best-effort). */
export function parseMalaysianAddress(full: string): ParsedMyAddress {
  const out: ParsedMyAddress = { ...EMPTY };
  const s = String(full || '').trim();
  if (!s) return out;

  const pc = s.match(POSTCODE_RE);
  if (pc) out.postcode = pc[1];

  let parts = s.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);

  // country — 마지막 파트가 국가명/코드일 때
  if (parts.length) {
    const last = parts[parts.length - 1];
    const resolved = resolveCountry(last);
    if (KNOWN_COUNTRIES.some((c) => c.toUpperCase() === last.toUpperCase()) || (resolved && /^B\d/i.test(last))) {
      out.country = resolved || last;
      parts.pop();
    }
  }

  // state — 뒤에서부터 주 이름 매칭
  for (let i = parts.length - 1; i >= 0; i--) {
    const st = matchState(parts[i]);
    if (st) {
      out.state = st;
      const rest = parts[i].replace(new RegExp(st, 'i'), '').replace(/^[,\s]+|[,\s]+$/g, '').trim();
      if (rest) parts[i] = rest; else parts.splice(i, 1);
      break;
    }
  }

  // town — postcode 가 포함된 파트의 비숫자 텍스트 (예: "55100 KUALA LUMPUR" → "KUALA LUMPUR")
  if (out.postcode) {
    const idx = parts.findIndex((p) => p.includes(out.postcode));
    if (idx >= 0) {
      const t = parts[idx].replace(out.postcode, '').replace(/^[,\s]+|[,\s]+$/g, '').trim();
      if (t && !ROAD_KW.test(t)) { out.town = t; parts.splice(idx, 1); }
      else if (!t) parts.splice(idx, 1);
      else parts[idx] = t;
    }
  }
  // town 미확정 시 — 도로 키워드 없는 뒤쪽 파트를 town 으로
  if (!out.town) {
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i] && !ROAD_KW.test(parts[i]) && !/^\d+$/.test(parts[i])) {
        out.town = parts[i]; parts.splice(i, 1); break;
      }
    }
  }

  out.street = parts.filter(Boolean).join(', ').trim();
  return out;
}

/** 구조화 주소 객체(Contact registeredAddress 형태) → 컴포넌트. 라벨 필드가 있어 더 신뢰도 높음. */
export function parseMyAddressObject(o: any): ParsedMyAddress {
  const out: ParsedMyAddress = { ...EMPTY };
  if (!o || typeof o !== 'object') return out;
  const src = Array.isArray(o) ? o[0] : o;
  if (!src || typeof src !== 'object') return out;

  out.postcode = String(src.postcode ?? src.post_code ?? src.POSTCODE ?? src.POSTAL_CODE ?? '').trim();
  out.state = String(src.state ?? src.STATE ?? '').trim();
  out.country = resolveCountry(src.country ?? src.COUNTRY ?? src.countryName ?? src.countryCode);

  const lines = [src.addressLine1, src.addressLine2, src.addressLine3, src.line1, src.line2]
    .map((x: any) => String(x || '').trim()).filter(Boolean);
  const city = String(src.city ?? src.CITY ?? src.town ?? '').trim();

  const streetLines = lines.filter((l) => ROAD_KW.test(l));
  const nonStreet = lines.filter((l) => !ROAD_KW.test(l));
  out.street = streetLines.join(', ').trim();
  out.town = city || nonStreet.join(', ').trim();

  // street 를 못 뽑았는데 라인이 있으면 — 라인 전체를 street 로, town 은 city 만
  if (!out.street && lines.length) {
    out.street = lines.join(', ');
    if (!city) out.town = '';
    else out.town = city;
  }
  return out;
}

export function isEmptyMyAddress(a?: ParsedMyAddress | null): boolean {
  return !a || (!a.street && !a.town && !a.state && !a.postcode && !a.country);
}

/**
 * MY 문서/폼용 주소 한 줄.
 * AddressInputModal / ChatMalaysiaAddressInput 과 동일 — 전체 대문자.
 */
export function formatMyAddressLine(
  parts: Array<string | null | undefined> | string,
): string {
  if (typeof parts === 'string') {
    return String(parts || '')
      .split(/[\n,]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .join(', ')
      .toUpperCase();
  }
  return parts
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(', ')
    .toUpperCase();
}

/** street / postcode+town / state / country → 문서용 한 줄(대문자). */
export function composeMyAddressLine(a: {
  line?: string;
  street?: string;
  town?: string;
  state?: string;
  postcode?: string;
  country?: string;
}): string {
  return formatMyAddressLine([
    a.line || a.street || '',
    [a.postcode, a.town].filter(Boolean).join(' '),
    a.state,
    a.country,
  ]);
}
